import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';
// Removed TypeScript interfaces

const initialState = {
  dashboardStats: {
    totalComplaints: 0,
    pendingComplaints: 0,
    resolvedComplaints: 0,
    totalCitizens: 0,
    recentComplaints: [],
    recentPosts: [],
  },
  loading: false,
  error: null,
  postLoading: false,
  postError: null,
  complaintUpdateLoading: false,
  complaintUpdateError: null,
  complaints: [],
  complaintsLoading: false,
  complaintsError: null,
  // New state for representatives by area
  representativesByArea: [],
  representativesByAreaLoading: false,
  representativesByAreaError: null,
  // User's saved address
  userSavedAddress: null,
  userSavedAddressLoading: false,
  userSavedAddressError: null,
};

// Async Thunks
export const fetchDashboardData = createAsyncThunk(
  'representative/fetchDashboardData',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/representatives/dashboard/${userId}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// New thunk to fetch representatives by area
export const fetchRepresentativesByArea = createAsyncThunk(
  'representative/fetchRepresentativesByArea',
  async (areaId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/representatives/by-area/${areaId}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch representatives');
    }
  }
);

export const fetchAllRepresentatives = createAsyncThunk(
  'representative/fetchAllRepresentatives',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/representatives/all');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch all representatives');
    }
  }
);

export const fetchUserSavedAddress = createAsyncThunk(
  'representative/fetchUserSavedAddress',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/users/${userId}/address`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user address');
    }
  }
);

export const createPost = createAsyncThunk(
  'representative/createPost',
  async (
    { content, type, areaId },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/posts', { content, type, areaId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateComplaintStatus = createAsyncThunk(
  'representative/updateComplaintStatus',
  async (
    { complaintId, status },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(`/complaints/${complaintId}/status`, { status });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createComplaintUpdate = createAsyncThunk(
  'representative/createComplaintUpdate',
  async (
    { complaintId, status, comment },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`/complaints/${complaintId}/updates`, { status, comment });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchComplaints = createAsyncThunk(
  'representative/fetchComplaints',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/complaints');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Slice
const representativeSlice = createSlice({
  name: 'representative',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchDashboardData
      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchRepresentativesByArea
      .addCase(fetchRepresentativesByArea.pending, (state) => {
        state.representativesByAreaLoading = true;
        state.representativesByAreaError = null;
      })
      .addCase(fetchRepresentativesByArea.fulfilled, (state, action) => {
        state.representativesByAreaLoading = false;
        state.representativesByArea = action.payload;
      })
      .addCase(fetchRepresentativesByArea.rejected, (state, action) => {
        state.representativesByAreaLoading = false;
        state.representativesByAreaError = action.payload;
      })

      // fetchAllRepresentatives
      .addCase(fetchAllRepresentatives.pending, (state) => {
        state.representativesByAreaLoading = true; // Reusing loading state for now
        state.representativesByAreaError = null;
      })
      .addCase(fetchAllRepresentatives.fulfilled, (state, action) => {
        state.representativesByAreaLoading = false;
        // Assuming action.payload is an array of all representatives
        // For now, we'll just set it to the payload
        state.representativesByArea = action.payload;
      })
      .addCase(fetchAllRepresentatives.rejected, (state, action) => {
        state.representativesByAreaLoading = false;
        state.representativesByAreaError = action.payload;
      })

      // fetchUserSavedAddress
      .addCase(fetchUserSavedAddress.pending, (state) => {
        state.userSavedAddressLoading = true;
        state.userSavedAddressError = null;
      })
      .addCase(fetchUserSavedAddress.fulfilled, (state, action) => {
        state.userSavedAddressLoading = false;
        state.userSavedAddress = action.payload;
      })
      .addCase(fetchUserSavedAddress.rejected, (state, action) => {
        state.userSavedAddressLoading = false;
        state.userSavedAddressError = action.payload;
      })

      // createPost
      .addCase(createPost.pending, (state) => {
        state.postLoading = true;
        state.postError = null;
      })
      .addCase(createPost.fulfilled, (state, action) => {
        state.postLoading = false;
        state.dashboardStats.recentPosts.unshift(action.payload);
      })
      .addCase(createPost.rejected, (state, action) => {
        state.postLoading = false;
        state.postError = action.payload;
      })

      // updateComplaintStatus
      .addCase(updateComplaintStatus.pending, (state) => {
        state.complaintUpdateLoading = true;
        state.complaintUpdateError = null;
      })
      .addCase(updateComplaintStatus.fulfilled, (state, action) => {
        state.complaintUpdateLoading = false;
        const updatedComplaint = action.payload;
        const index = state.dashboardStats.recentComplaints.findIndex(
          (c) => c.id === updatedComplaint.id
        );
        if (index !== -1) {
          state.dashboardStats.recentComplaints[index] = updatedComplaint;
        }
      })
      .addCase(updateComplaintStatus.rejected, (state, action) => {
        state.complaintUpdateLoading = false;
        state.complaintUpdateError = action.payload;
      })

      // fetchComplaints
      .addCase(fetchComplaints.pending, (state) => {
        state.complaintsLoading = true;
        state.complaintsError = null;
      })
      .addCase(fetchComplaints.fulfilled, (state, action) => {
        state.complaintsLoading = false;
        state.complaints = action.payload;
      })
      .addCase(fetchComplaints.rejected, (state, action) => {
        state.complaintsLoading = false;
        state.complaintsError = action.payload;
      });
  },
});

export default representativeSlice.reducer;
